<template>
  <div class="min-h-screen bg-neutral-50 pt-8 pb-16">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col">
      <!-- 板块标题 -->
      <div
        class="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-3"
      >
        <div>
          <h1 class="text-2xl md:text-3xl font-bold text-neutral-900">
            我的AI工作台
          </h1>
          <p class="text-sm text-neutral-500 mt-1">
            这里汇集你在 AI应用广场 加入的应用、自己的创作与账号数据
          </p>
        </div>
        <div class="flex items-center gap-2 flex-wrap self-start md:self-auto">
          <button
            class="inline-flex items-center px-3.5 py-2 rounded-lg bg-white border border-neutral-200 text-sm text-neutral-700 hover:border-primary hover:text-primary transition-colors"
            @click="selectTab('myagent')"
          >
            <i class="fa fa-robot mr-1"></i>我的智能体
          </button>
          <button
            class="inline-flex items-center px-3.5 py-2 rounded-lg bg-white border border-neutral-200 text-sm text-neutral-700 hover:border-primary hover:text-primary transition-colors"
            @click="selectTab('knowledgechain')"
          >
            <i class="fa fa-share-alt mr-1"></i>我的知识链
          </button>
          <router-link
            to="/workbench"
            class="inline-flex items-center px-4 py-2 rounded-lg bg-primary text-white text-sm hover:opacity-90 transition-opacity"
          >
            <i class="fa fa-window-maximize mr-1"></i>我的 HTML 工作台
          </router-link>
        </div>
      </div>

      <!-- 个人信息卡片 -->
      <div
        class="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6 md:p-8 mb-8 flex flex-col md:flex-row items-center md:items-start gap-6 relative overflow-hidden"
      >
        <!-- 背景装饰 -->
        <div
          class="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"
        ></div>

        <!-- 头像 -->
        <div class="relative group">
          <img
            :src="user.avatar"
            alt="用户头像"
            class="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-4 border-white shadow-md"
          />
          <button
            @click="triggerAvatarUpload"
            class="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full shadow border border-neutral-200 flex items-center justify-center text-neutral-600 hover:text-primary transition-colors"
          >
            <i class="fa fa-camera text-xs"></i>
          </button>
          <!-- 隐藏的文件选择器 -->
          <input
            ref="avatarInput"
            type="file"
            accept="image/png, image/jpeg, image/jpg, image/webp"
            class="hidden"
            @change="handleAvatarUpload"
          />
        </div>

        <!-- 信息 -->
        <div class="flex-1 text-center md:text-left z-10">
          <div
            class="flex flex-col md:flex-row items-center md:items-baseline gap-3 mb-2"
          >
            <h1 class="text-2xl font-bold text-neutral-900">
              {{ user.name }}
            </h1>
            <!-- 智点赛道（科技蓝） -->
            <span
              class="group relative px-2 py-0.5 text-xs font-medium rounded-full border cursor-default"
              style="background: #e8f2ff; color: #0b5fd0; border-color: #b6d4ff"
            >
              <i class="fa fa-code mr-1"></i>LV.{{ levelInfo.devLevel }}
              {{ levelInfo.devLevelName }}
              <span
                class="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1.5 whitespace-nowrap rounded bg-neutral-900/90 px-2 py-1 text-[11px] font-normal text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100"
                >{{ levelInfo.devTooltip }} · 累计智点
                {{ levelInfo.devPoint || 0 }}<template v-if="levelInfo.devNext > 0"
                  >，再获得 {{ levelInfo.devNext }} 智点升级</template
                ></span
              >
            </span>

            <!-- 创作赛道（活力橙） -->
            <span
              class="group relative px-2 py-0.5 text-xs font-medium rounded-full border cursor-default"
              style="background: #fff1e6; color: #c2410c; border-color: #ffcba4"
            >
              <i class="fa fa-paint-brush mr-1"></i>LV.{{ levelInfo.userLevel }}
              {{ levelInfo.userLevelName }}
              <span
                class="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1.5 whitespace-nowrap rounded bg-neutral-900/90 px-2 py-1 text-[11px] font-normal text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100"
                >{{ levelInfo.userTooltip }} · 累计作品
                {{ levelInfo.userToolCount || 0 }} 个<template
                  v-if="levelInfo.userNext > 0"
                  >，再 {{ levelInfo.userNext }} 个作品升级</template
                ></span
              >
            </span>
          </div>
          <p class="text-neutral-500 text-sm mb-4">
            <!-- 加入时间：{{ state.user.joinDate }} -->
          </p>
          <div class="flex flex-wrap justify-center md:justify-start gap-2">
            <span
              v-for="b in badgeList()"
              :key="b"
              class="px-3 py-1 bg-neutral-100 rounded-full text-xs text-neutral-600"
              >{{ b }}</span
            >
            <span
              v-if="!badgeList().length"
              class="px-3 py-1 bg-neutral-100 rounded-full text-xs text-neutral-600"
              >{{ levelInfo.levelName || "AI 爱好者" }}</span
            >
            <!-- <span
              class="px-3 py-1 bg-neutral-100 rounded-full text-xs text-neutral-600"
              >全栈开发</span
            > -->
            <button
              @click="openBioDialog"
              title="编辑个人介绍"
              class="w-6 h-6 rounded-full border border-neutral-300 flex items-center justify-center text-neutral-400 hover:border-primary hover:text-primary transition-colors text-xs"
            >
              <i class="fa fa-plus"></i>
            </button>
            <span
              v-if="levelInfo.bio"
              class="px-3 py-1 bg-neutral-50 rounded-full text-xs text-neutral-500 border border-neutral-200 max-w-xs truncate"
              >{{ levelInfo.bio }}</span
            >
          </div>
        </div>

        <!-- 数据概览 -->
        <div class="flex gap-4 md:gap-8 mt-4 md:mt-0 z-10">
          <div class="text-center">
            <div class="text-2xl font-bold text-neutral-900">
              {{ levelInfo.wisdom }}
            </div>
            <div class="text-xs text-neutral-500">智点</div>
          </div>
          <div class="w-px bg-neutral-200 h-10 self-center"></div>
          <div class="text-center">
            <div class="text-2xl font-bold text-neutral-900">
              {{ user.points }}
            </div>
            <div class="text-xs text-neutral-500">创值</div>
          </div>
          <div class="w-px bg-neutral-200 h-10 self-center"></div>
          <div class="text-center">
            <div class="text-2xl font-bold text-neutral-900">
              {{ myToolList.length }}
            </div>
            <div class="text-xs text-neutral-500">发布</div>
          </div>
          <div class="w-px bg-neutral-200 h-10 self-center"></div>
          <div class="text-center">
            <div class="text-2xl font-bold text-neutral-900">
              {{ mineTotalAll }}
            </div>
            <div class="text-xs text-neutral-500">我的应用</div>
          </div>
        </div>

        <!-- 编辑按钮 -->
        <!-- <button
          class="absolute top-6 right-6 text-neutral-400 hover:text-primary transition-colors md:block hidden"
        >
          <i class="fa fa-cog text-xl"></i>
        </button> -->
      </div>

      <!-- 我的作品：按作品维度展示收藏 / 点赞 / 下载 / 浏览 -->
      <div
        class="order-3 bg-white rounded-2xl border border-neutral-200 p-5 md:p-6 mb-8"
      >
        <div class="flex items-center justify-between mb-3">
          <div>
            <div class="flex items-center gap-2 flex-wrap">
              <h2 class="text-base md:text-lg font-bold text-neutral-900">
                我的作品
              </h2>
              <span
                class="px-2 py-0.5 text-[11px] font-medium rounded-full border"
                style="background: #e8f2ff; color: #0b5fd0; border-color: #b6d4ff"
              >
                <i class="fa fa-code mr-1"></i>LV.{{ levelInfo.devLevel }}
                {{ levelInfo.devLevelName }}
              </span>
              <span
                v-if="levelInfo.devNext > 0"
                class="text-[11px] text-neutral-400"
                >再获得 {{ levelInfo.devNext }} 智点升级</span
              >
            </div>
            <p class="text-xs text-neutral-400 mt-0.5">
              你发布的工具，看看它们的收藏、点赞与使用情况
            </p>
          </div>
          <button
            @click="loadMyWorks"
            class="w-9 h-9 rounded-lg border border-neutral-200 text-neutral-500 hover:border-primary hover:text-primary transition-colors"
            title="刷新数据"
          >
            <i class="fa fa-refresh" :class="{ 'fa-spin': worksLoading }"></i>
          </button>
        </div>

        <div
          v-if="worksLoading"
          class="py-8 text-center text-sm text-neutral-400"
        >
          <i class="fa fa-spinner fa-spin mr-2"></i>加载中…
        </div>

        <div v-else-if="works.length" class="space-y-1">
          <div
            v-for="w in works"
            :key="w.id"
            class="flex items-center gap-3 p-2.5 rounded-xl hover:bg-neutral-50 transition-colors cursor-pointer"
            @click="openWork(w)"
          >
            <div
              class="w-11 h-11 rounded-lg bg-neutral-100 overflow-hidden flex items-center justify-center flex-shrink-0 text-neutral-400"
            >
              <img
                v-if="w.cover"
                :src="w.cover"
                alt=""
                class="w-full h-full object-cover"
                @error="$event.target.style.display = 'none'"
              />
              <i v-else class="fa fa-cube"></i>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <span
                  class="text-sm font-medium text-neutral-900 line-clamp-1"
                  >{{ w.title || "未命名" }}</span
                >
                <span
                  class="text-[11px] px-1.5 py-0.5 rounded-full flex-shrink-0"
                  :class="
                    w.status === 1
                      ? 'bg-emerald-50 text-emerald-600'
                      : 'bg-amber-50 text-amber-600'
                  "
                >
                  {{ w.status === 1 ? "已上架" : "待审核" }}
                </span>
              </div>
              <div
                class="text-[11px] text-neutral-400 mt-1 flex flex-wrap gap-x-3"
              >
                <span
                  ><i class="fa fa-heart-o mr-1"></i>收藏
                  {{ w.favCount || 0 }}</span
                >
                <span
                  ><i class="fa fa-thumbs-o-up mr-1"></i>点赞
                  {{ w.zanCount || 0 }}</span
                >
                <span
                  ><i class="fa fa-download mr-1"></i>下载
                  {{ w.downloadCount || 0 }}</span
                >
                <span
                  ><i class="fa fa-eye mr-1"></i>浏览
                  {{ w.viewCount || 0 }}</span
                >
              </div>
            </div>
            <i class="fa fa-angle-right text-neutral-300"></i>
          </div>
        </div>

        <div v-else class="py-8 text-center">
          <p class="text-sm text-neutral-500 mb-3">还没有发布作品</p>
          <router-link
            to="/submit"
            class="inline-block px-4 py-1.5 rounded-lg bg-primary text-white text-xs hover:bg-primary/90"
          >
            发布工具
          </router-link>
        </div>

        <!-- 快捷入口 -->
        <div
          class="flex flex-wrap gap-2 mt-5 pt-4 border-t border-neutral-100"
        >
          <router-link
            to="/square"
            class="px-3.5 py-1.5 rounded-lg bg-neutral-100 text-xs text-neutral-600 hover:bg-primary hover:text-white transition-colors"
          >
            <i class="fa fa-th-large mr-1"></i>去应用广场
          </router-link>
          <router-link
            to="/create"
            class="px-3.5 py-1.5 rounded-lg bg-neutral-100 text-xs text-neutral-600 hover:bg-primary hover:text-white transition-colors"
          >
            <i class="fa fa-lightbulb-o mr-1"></i>去创想广场
          </router-link>
          <router-link
            to="/submit"
            class="px-3.5 py-1.5 rounded-lg bg-neutral-100 text-xs text-neutral-600 hover:bg-primary hover:text-white transition-colors"
          >
            <i class="fa fa-plus-circle mr-1"></i>发布工具
          </router-link>
        </div>
      </div>

      <!-- 算力阶序说明（页面固定展示） -->
      <div
        class="order-4 bg-white rounded-2xl border border-neutral-200 p-5 mb-8 text-xs leading-relaxed text-neutral-600"
      >
        <div class="flex items-center gap-2 mb-3">
          <i class="fa fa-signal text-primary"></i>
          <span class="font-bold text-sm text-neutral-900">算力阶序说明</span>
        </div>
        <p class="mb-1.5">
          【智点】算力阶序升级凭证，仅登录、发布、使用应用可获取，购买创值不增加智点。
        </p>
        <p class="mb-1.5">
          【创值】集市兑换代币，支持充值，前往「创值兑仓」兑换福利；创值无法转化为智点。
        </p>
        <p class="text-neutral-500">
          兑换窗口：每周六、周日开放兑换，每月设有兑换上限。
        </p>
      </div>

      <!-- 个人介绍弹窗 -->
      <el-dialog v-model="bioDialogVisible" title="个人介绍" width="480px">
        <div class="mb-3 text-xs text-neutral-500" v-if="levelInfo.bio && !bioEditing">
          {{ levelInfo.bio }}
        </div>
        <div class="mb-3 text-xs text-neutral-400" v-else-if="!bioEditing">
          还没有个人介绍，点击下方「编辑」写点什么吧～
        </div>
        <el-input
          v-if="bioEditing"
          v-model="bioText"
          type="textarea"
          :rows="4"
          maxlength="200"
          show-word-limit
          placeholder="介绍一下自己（最多200字）"
        />
        <template #footer>
          <template v-if="!bioEditing">
            <el-button @click="bioDialogVisible = false">关闭</el-button>
            <el-button type="primary" @click="bioEditing = true; bioText = levelInfo.bio || ''"
              >编辑</el-button
            >
          </template>
          <template v-else>
            <el-button @click="bioEditing = false">取消</el-button>
            <el-button type="primary" :loading="bioSaving" @click="saveBio">保存</el-button>
          </template>
        </template>
      </el-dialog>

      <!-- 主要内容区 -->
      <div class="order-2 flex flex-col lg:flex-row gap-8">
        <!-- 左侧导航 -->
        <div class="w-full lg:w-64 flex-shrink-0">
          <div
            class="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden sticky top-24"
          >
            <nav class="flex flex-col p-2">
              <button
                @click="goMessages"
                class="flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all mb-1 text-neutral-600 hover:bg-neutral-50"
              >
                <i class="fa fa-bell-o w-6 text-center mr-2"></i>
                消息中心
                <span
                  v-if="unreadTotal > 0"
                  class="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[11px] leading-[18px] text-center"
                  >{{ unreadTotal > 99 ? "99+" : unreadTotal }}</span
                >
              </button>
              <!-- 应用：可展开，默认展开，右侧平铺展示 -->
              <button
                @click="toggleAppGroup"
                class="flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all mt-1"
                :class="
                  currentTab === 'mine'
                    ? 'text-primary'
                    : 'text-neutral-600 hover:bg-neutral-50'
                "
              >
                <i class="fa fa-th-large w-6 text-center mr-2"></i>
                应用
                <i
                  class="fa ml-auto text-xs opacity-70"
                  :class="appOpen ? 'fa-angle-down' : 'fa-angle-right'"
                ></i>
              </button>
              <div v-show="appOpen" class="pl-4">
                <button
                  v-for="f in mineFilters"
                  :key="f.id"
                  @click="selectMineTab(f.id)"
                  :class="[
                    'w-full flex items-center px-3 py-2 rounded-lg text-[13px] transition-all mb-0.5',
                    currentTab === 'mine' && mineFilter === f.id
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-neutral-500 hover:bg-neutral-50',
                  ]"
                >
                  <i :class="['fa w-5 text-center mr-2', f.icon]"></i>
                  {{ f.name }}
                  <span
                    v-if="mineCounts[f.id]"
                    class="ml-auto text-[11px] text-neutral-400"
                    >{{ mineCounts[f.id] }}</span
                  >
                </button>
              </div>

              <!-- 创作：在右侧展示发布入口，不跳转创想广场 -->
              <button
                @click="selectCreateTab"
                class="flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all mt-1"
                :class="
                  currentTab === 'create'
                    ? 'bg-primary/10 text-primary'
                    : 'text-neutral-600 hover:bg-neutral-50'
                "
              >
                <i class="fa fa-lightbulb-o w-6 text-center mr-2"></i>
                创作
                <i class="fa fa-angle-right ml-auto text-xs opacity-60"></i>
              </button>

              <template v-for="g in tabGroups" :key="g.id">
                <p
                  class="px-4 pt-3 pb-1 text-[11px] font-bold text-neutral-400 tracking-wide"
                >
                  {{ g.name }}
                </p>
                <button
                  v-for="tab in tabsByGroup(g.id)"
                  :key="tab.id"
                  @click="selectTab(tab.id)"
                  :class="[
                    'flex items-center px-4 py-2.5 rounded-lg text-sm font-medium transition-all mb-1',
                    currentTab === tab.id
                      ? 'bg-primary/10 text-primary'
                      : 'text-neutral-600 hover:bg-neutral-50',
                  ]"
                >
                  <i :class="['fa w-6 text-center mr-2', tab.icon]"></i>
                  {{ tab.name }}
                </button>
              </template>
              <button
                v-if="isAdmin"
                @click="$router.push('/admin')"
                class="flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all mb-1 text-white bg-primary hover:bg-primary/90 mt-1"
              >
                <i class="fa fa-shield w-6 text-center mr-2"></i>
                管理后台
                <i class="fa fa-angle-right ml-auto text-xs opacity-70"></i>
              </button>
            </nav>
            <div class="border-t border-neutral-100 p-2 mt-2">
              <button
                @click="logout"
                class="w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 transition-all"
              >
                <i class="fa fa-sign-out w-6 text-center mr-2"></i>
                退出登录
              </button>
              <button
                @click="deleteAccount"
                class="w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium text-neutral-400 hover:bg-neutral-50 transition-all"
              >
                <i class="fa fa-user-times w-6 text-center mr-2"></i>
                注销账号
              </button>
            </div>
          </div>
        </div>

        <!-- 右侧内容 -->
        <div class="flex-1 min-w-0">
          <!-- 我的应用（默认展示）：应用广场里点「＋」加入的内容，平铺呈现 -->
          <div v-if="currentTab === 'mine'" class="space-y-6">
            <div class="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <h2 class="text-lg font-bold text-neutral-900">
                  {{ mineFilterName }}
                </h2>
                <p class="text-xs text-neutral-400 mt-1">
                  <template v-if="homePref">
                    已把「{{ homePref.title || "指定内容" }}」设为首页，打开网站会直接进入
                    <button
                      @click="cancelHomePref"
                      class="ml-1 text-primary hover:underline"
                    >
                      取消
                    </button>
                  </template>
                  <template v-else>
                    你在应用广场点「＋」加入的内容都平铺在这里，点卡片上的 ★ 可设为首页
                  </template>
                </p>
              </div>
              <div class="text-sm text-neutral-500">
                共 {{ mineTotal }} 个
              </div>
            </div>

            <div
              v-if="mineLoading"
              class="bg-white rounded-xl border border-neutral-200 p-12 text-center text-neutral-400 text-sm"
            >
              <i class="fa fa-spinner fa-spin mr-2"></i>加载中…
            </div>

            <div
              v-else-if="mineList.length > 0"
              class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
            >
              <div
                v-for="it in mineList"
                :key="it.type + '_' + it.id"
                class="bg-white rounded-xl border border-neutral-200 p-4 hover:shadow-md hover:border-primary/40 transition-all cursor-pointer flex flex-col"
                @click="openMineItem(it)"
              >
                <div class="flex items-start gap-3 mb-2">
                  <div
                    class="w-11 h-11 rounded-xl bg-neutral-100 overflow-hidden flex items-center justify-center flex-shrink-0 text-neutral-400"
                  >
                    <img
                      v-if="it.cover"
                      :src="it.cover"
                      alt=""
                      class="w-full h-full object-cover"
                      @error="$event.target.style.display = 'none'"
                    />
                    <i
                      v-else
                      class="fa"
                      :class="mineTypeIcon(it.type)"
                    ></i>
                  </div>
                  <div class="min-w-0 flex-1">
                    <h3
                      class="font-semibold text-sm text-neutral-900 line-clamp-1"
                    >
                      {{ it.title || "未命名" }}
                    </h3>
                    <div class="flex items-center gap-1 mt-1">
                      <span
                        class="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary"
                        >{{ it.typeName }}</span
                      >
                      <span
                        v-if="it.tag"
                        class="text-[11px] text-neutral-400 truncate"
                        >{{ it.tag }}</span
                      >
                    </div>
                  </div>
                  <div class="flex items-center gap-1 flex-shrink-0">
                    <button
                      @click.stop="setHomePref(it)"
                      class="w-7 h-7 rounded-full flex items-center justify-center transition-colors"
                      :class="
                        isHomePref(it)
                          ? 'text-amber-500 bg-amber-50'
                          : 'text-neutral-300 hover:text-amber-500 hover:bg-amber-50'
                      "
                      :title="
                        isHomePref(it)
                          ? '当前个人首页'
                          : '设为首页（打开网站直接进入）'
                      "
                    >
                      <i
                        class="text-xs"
                        :class="isHomePref(it) ? 'fa fa-star' : 'fa fa-star-o'"
                      ></i>
                    </button>
                    <button
                      @click.stop="removeMineItem(it)"
                      class="w-7 h-7 rounded-full text-neutral-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center"
                      title="移出我的应用"
                    >
                      <i class="fa fa-times text-xs"></i>
                    </button>
                  </div>
                </div>
                <p class="text-xs text-neutral-500 line-clamp-2 flex-1">
                  {{ it.desc || "暂无简介" }}
                </p>
              </div>
            </div>

            <div
              v-else
              class="bg-white rounded-xl border border-neutral-200 p-12 text-center"
            >
              <div
                class="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4 text-neutral-400"
              >
                <i class="fa fa-th-large text-2xl"></i>
              </div>
              <h3 class="text-lg font-medium text-neutral-900 mb-2">
                我的应用还是空的
              </h3>
              <p class="text-neutral-500 text-sm mb-6">
                去应用广场逛一逛，点卡片右上角的「＋」就能加入这里
              </p>
              <router-link
                to="/explore"
                class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                去应用广场
              </router-link>
            </div>

            <div v-if="mineTotal > minePageSize" class="flex justify-center">
              <el-pagination
                background
                layout="prev, pager, next"
                :total="mineTotal"
                :page-size="minePageSize"
                :current-page="minePage"
                @current-change="minePageChange"
              />
            </div>
          </div>

          <!-- 创作：四个发布入口，不跳转创想广场 -->
          <div v-if="currentTab === 'create'" class="space-y-6">
            <div>
              <h2 class="text-lg font-bold text-neutral-900">创作</h2>
              <p class="text-xs text-neutral-400 mt-1">
                把你的作品发布出来，或提出你想要的教学工具
              </p>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
              <button
                v-for="p in publishEntries"
                :key="p.to"
                @click="goPublish(p)"
                class="group bg-white rounded-xl border border-neutral-200 p-4 md:p-5 text-left hover:border-primary hover:shadow-md transition-all"
              >
                <div class="flex items-center gap-2 mb-2">
                  <span
                    class="w-9 h-9 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                    :class="p.bg"
                  >
                    <i :class="['fa', p.icon]" class="text-sm"></i>
                  </span>
                  <span class="font-bold text-sm md:text-base text-neutral-900">{{
                    p.label
                  }}</span>
                </div>
                <p class="text-xs text-neutral-400 leading-relaxed">
                  {{ p.desc }}
                </p>
              </button>
            </div>

            <div class="bg-white rounded-xl border border-neutral-200 p-5">
              <div class="text-xs font-bold text-neutral-500 mb-3">我的创作</div>
              <div class="flex flex-wrap gap-2">
                <button
                  @click="selectTab('submissions')"
                  class="px-3.5 py-1.5 rounded-lg bg-neutral-100 text-xs text-neutral-600 hover:bg-primary hover:text-white transition-colors"
                >
                  <i class="fa fa-cube mr-1"></i>我发布的工具与案例
                </button>
                <button
                  @click="selectTab('requests')"
                  class="px-3.5 py-1.5 rounded-lg bg-neutral-100 text-xs text-neutral-600 hover:bg-primary hover:text-white transition-colors"
                >
                  <i class="fa fa-lightbulb-o mr-1"></i>我提出的需求
                </button>
                <router-link
                  to="/create"
                  class="px-3.5 py-1.5 rounded-lg bg-neutral-100 text-xs text-neutral-500 hover:bg-primary hover:text-white transition-colors"
                >
                  <i class="fa fa-globe mr-1"></i>去创想广场看看
                </router-link>
              </div>
            </div>
          </div>

          <!-- 案例收藏 -->
          <div v-if="currentTab === 'casefav'" class="space-y-6">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-lg font-bold text-neutral-900">案例收藏</h2>
              <div class="text-sm text-neutral-500">
                共 {{ caseFavTotal }} 个案例
              </div>
            </div>

            <div
              v-if="caseFavList.length > 0"
              class="grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              <div
                v-for="c in caseFavList"
                :key="c.id"
                class="bg-white rounded-xl border border-neutral-200 p-5 hover-lift cursor-pointer group flex flex-col"
                @click="goCase"
              >
                <div class="flex items-start justify-between mb-2">
                  <h3
                    class="font-bold text-neutral-900 line-clamp-2 flex-1 group-hover:text-primary transition-colors"
                  >
                    {{ c.title }}
                  </h3>
                  <button
                    @click.stop="removeCaseFav(c)"
                    class="ml-2 w-8 h-8 rounded-full text-red-500 hover:bg-red-50 flex items-center justify-center flex-shrink-0"
                    title="取消收藏"
                  >
                    <i class="fa fa-heart"></i>
                  </button>
                </div>
                <p class="text-neutral-500 text-xs line-clamp-2 flex-1">
                  {{ c.scenario || "暂无场景说明" }}
                </p>
                <div
                  class="flex items-center justify-between text-xs text-neutral-400 mt-3"
                >
                  <span
                    ><i class="fa fa-user-o mr-1"></i>{{
                      c.author || "匿名"
                    }}</span
                  >
                  <span
                    ><i class="fa fa-picture-o mr-1"></i>{{ c.imgCount || 0 }}
                    张图</span
                  >
                </div>
              </div>
              <div class="flex justify-end w-full col-span-1 md:col-span-3">
                <el-pagination
                  background
                  layout="prev, pager, next"
                  :total="caseFavTotal"
                  :page-size="9"
                  :current-page="caseFavPage"
                  @current-change="pageChange($event, 'casefav')"
                />
              </div>
            </div>

            <div
              v-else
              class="bg-white rounded-xl border border-neutral-200 p-12 text-center"
            >
              <div
                class="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4 text-neutral-400"
              >
                <i class="fa fa-graduation-cap text-2xl"></i>
              </div>
              <h3 class="text-lg font-medium text-neutral-900 mb-2">
                还没有收藏案例
              </h3>
              <p class="text-neutral-500 text-sm mb-6">
                在案例广场看到好的案例，点右上角的心形即可收藏到这里
              </p>
              <router-link
                to="/cases"
                class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                去案例广场
              </router-link>
            </div>
          </div>

          <!-- 智能体收藏 -->
          <div
            v-else-if="currentTab === 'agentfav'"
            class="space-y-6"
          >
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-lg font-bold text-neutral-900">智能体收藏</h2>
              <div class="text-sm text-neutral-500">
                共 {{ agentFavTotal }} 个智能体
              </div>
            </div>

            <div
              v-if="agentFavList.length > 0"
              class="grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              <div
                v-for="a in agentFavList"
                :key="a.id"
                class="bg-white rounded-xl border border-neutral-200 p-5 hover-lift cursor-pointer group flex flex-col"
                @click="goAgent(a)"
              >
                <div class="flex items-start mb-3">
                  <div
                    class="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-blue-400 flex items-center justify-center text-white text-lg font-bold flex-shrink-0 overflow-hidden"
                  >
                    <img
                      v-if="a.avatar"
                      :src="a.avatar"
                      class="w-full h-full object-cover"
                      @error="$event.target.style.display = 'none'"
                    />
                    <span v-else>{{ agentFirstChar(a.name) }}</span>
                  </div>
                  <div class="ml-3 min-w-0 flex-1">
                    <h3
                      class="font-bold text-neutral-900 line-clamp-1 group-hover:text-primary transition-colors"
                    >
                      {{ a.name }}
                    </h3>
                    <p class="text-xs text-neutral-400">
                      {{ a.useCount || 0 }} 次对话
                    </p>
                  </div>
                  <button
                    @click.stop="removeAgentFav(a)"
                    class="w-8 h-8 rounded-full text-red-500 hover:bg-red-50 flex items-center justify-center flex-shrink-0"
                    title="取消收藏"
                  >
                    <i class="fa fa-heart"></i>
                  </button>
                </div>
                <p class="text-neutral-500 text-xs line-clamp-2 flex-1">
                  {{ a.intro || "暂无简介" }}
                </p>
                <div class="mt-3">
                  <span
                    class="inline-flex items-center px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-medium group-hover:bg-primary group-hover:text-white transition-colors"
                  >
                    开始对话 <i class="fa fa-arrow-right ml-1"></i>
                  </span>
                </div>
              </div>
              <div class="flex justify-end w-full col-span-1 md:col-span-3">
                <el-pagination
                  background
                  layout="prev, pager, next"
                  :total="agentFavTotal"
                  :page-size="9"
                  :current-page="agentFavPage"
                  @current-change="pageChange($event, 'agentfav')"
                />
              </div>
            </div>

            <div
              v-else
              class="bg-white rounded-xl border border-neutral-200 p-12 text-center"
            >
              <div
                class="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4 text-neutral-400"
              >
                <i class="fa fa-robot text-2xl"></i>
              </div>
              <h3 class="text-lg font-medium text-neutral-900 mb-2">
                还没有收藏智能体
              </h3>
              <p class="text-neutral-500 text-sm mb-6">
                在智能体广场看到好用的智能体，点右上角的心形即可收藏到这里
              </p>
              <router-link
                to="/agents"
                class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                去智能体广场
              </router-link>
            </div>
          </div>

          <!-- 收藏列表 -->
          <div v-if="currentTab === 'favorites'" class="space-y-6">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-lg font-bold text-neutral-900">我的收藏</h2>
              <div class="text-sm text-neutral-500">
                共 {{ favTotal }} 个工具
              </div>
            </div>

            <div
              v-if="favToolList.length > 0"
              class="grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              <div
                v-for="tool in favToolList"
                :key="tool.id"
                class="bg-white rounded-xl border border-neutral-200 overflow-hidden hover-lift cursor-pointer group flex flex-col h-full"
                @click="openToolDetail(tool)"
              >
                <div class="h-40 overflow-hidden relative">
                  <img
                    :src="tool.cover"
                    :alt="tool.title"
                    class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div class="absolute top-2 right-2">
                    <button
                      @click.stop="toggleFavorite(tool.id)"
                      class="w-8 h-8 rounded-full bg-white/90 backdrop-blur text-red-500 flex items-center justify-center hover:bg-white shadow-sm transition-all"
                    >
                      <i class="fa fa-heart"></i>
                    </button>
                  </div>
                </div>
                <div class="p-4 flex-1 flex flex-col">
                  <div class="flex justify-between items-start mb-2">
                    <h3 class="font-bold text-neutral-900 line-clamp-1">
                      {{ tool.title }}
                    </h3>
                    <div
                      class="flex items-center text-xs text-yellow-500 bg-yellow-50 px-1.5 py-0.5 rounded"
                    >
                      <i class="fa fa-star mr-1"></i>{{ tool.rating }}
                    </div>
                  </div>
                  <p class="text-neutral-500 text-xs mb-3 line-clamp-2 flex-1">
                    {{ tool.description }}
                  </p>
                  <div
                    class="flex items-center justify-between text-xs text-neutral-400 mt-auto"
                  >
                    <!-- <span>{{ tool.category_id }}</span> -->
                    <!-- 实际应该显示分类名 -->
                    <span
                      ><i class="fa fa-download mr-1"></i
                      >{{ tool.downloadCount }}</span
                    >
                  </div>
                </div>
              </div>
              <div class="flex justify-end w-full col-span-1 md:col-span-3">
                <el-pagination
                  background
                  layout="prev, pager, next"
                  :total="favTotal"
                  @current-change="pageChange($event, 'favorites')"
                />
              </div>
            </div>

            <div
              v-else
              class="bg-white rounded-xl border border-neutral-200 p-12 text-center"
            >
              <div
                class="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4 text-neutral-400"
              >
                <i class="fa fa-heart-o text-2xl"></i>
              </div>
              <h3 class="text-lg font-medium text-neutral-900 mb-2">
                暂无收藏
              </h3>
              <p class="text-neutral-500 text-sm mb-6">
                你还没有收藏任何工具，去探索一下吧！
              </p>
              <router-link
                to="/explore"
                class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                探索工具
              </router-link>
            </div>
          </div>

          <!-- 提示词 / 技巧收藏列表 -->
          <div v-else-if="currentTab === 'promptfav'" class="space-y-6">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-lg font-bold text-neutral-900">我的提示词收藏</h2>
              <div class="text-sm text-neutral-500">
                共 {{ promptFavTotal }} 条内容
              </div>
            </div>

            <div v-if="promptFavList.length > 0" class="space-y-4">
              <div
                v-for="item in promptFavList"
                :key="item.id"
                class="bg-white rounded-xl border border-neutral-200 p-4 hover-lift cursor-pointer group"
                @click="openPromptFav(item)"
              >
                <div class="flex items-start">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <span
                        class="text-xs px-2 py-0.5 rounded-full"
                        :class="item.type === 'skill'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-100 text-blue-700'"
                      >
                        {{ item.type === "skill" ? "技巧分享" : "提示词" }}
                      </span>
                      <span class="text-xs text-neutral-400">{{
                        item.category
                      }}</span>
                    </div>
                    <h3 class="font-semibold text-neutral-900 truncate">
                      {{ item.title }}
                    </h3>
                    <p class="text-sm text-neutral-500 line-clamp-2 mt-1">
                      {{ item.content }}
                    </p>
                    <div
                      class="flex items-center gap-4 text-xs text-neutral-400 mt-2"
                    >
                      <span class="text-red-400">
                        <i class="fa fa-thumbs-o-up mr-1"></i
                        >{{ item.likeCount || 0 }}
                      </span>
                      <span>
                        <i class="fa fa-eye mr-1"></i>{{ item.viewCount || 0 }}
                      </span>
                      <span>{{ item.userName || "匿名用户" }}</span>
                    </div>
                  </div>
                  <button
                    @click.stop="cancelPromptFav(item)"
                    class="ml-4 w-9 h-9 rounded-full bg-neutral-100 text-amber-500 flex items-center justify-center hover:bg-amber-50 transition-colors flex-shrink-0"
                    title="取消收藏"
                  >
                    <i class="fa fa-heart"></i>
                  </button>
                </div>
              </div>

              <div
                v-if="promptFavTotal > 10"
                class="flex justify-end w-full mt-2"
              >
                <el-pagination
                  background
                  layout="prev, pager, next"
                  :total="promptFavTotal"
                  :page-size="10"
                  :current-page="promptFavPage"
                  @current-change="pageChange($event, 'promptfav')"
                />
              </div>
            </div>

            <div
              v-else
              class="bg-white rounded-xl border border-neutral-200 p-12 text-center"
            >
              <div
                class="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4 text-neutral-400"
              >
                <i class="fa fa-bookmark-o text-2xl"></i>
              </div>
              <h3 class="text-lg font-medium text-neutral-900 mb-2">
                暂无收藏的提示词
              </h3>
              <p class="text-neutral-500 text-sm mb-6">
                在提示词详情页点「收藏」，之后就能在这里快速找到
              </p>
              <router-link
                to="/prompts"
                class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                去逛逛
              </router-link>
            </div>
          </div>

          <!-- 发布列表：工具 / 案例 / 需求 / 提示词 聚合 -->
          <div v-else-if="currentTab === 'submissions'" class="space-y-5">
            <div class="flex items-center justify-between flex-wrap gap-2">
              <h2 class="text-lg font-bold text-neutral-900">我的发布</h2>
              <div class="flex items-center gap-3 text-sm">
                <router-link to="/submit" class="text-primary hover:underline font-medium">
                  <i class="fa fa-plus mr-1"></i>工具
                </router-link>
                <router-link to="/cases/submit" class="text-primary hover:underline font-medium">
                  <i class="fa fa-plus mr-1"></i>案例
                </router-link>
                <router-link to="/request" class="text-primary hover:underline font-medium">
                  <i class="fa fa-plus mr-1"></i>需求
                </router-link>
              </div>
            </div>

            <div class="flex flex-wrap gap-2">
              <button
                v-for="t in subTypes"
                :key="t.id"
                class="px-3 py-1.5 rounded-lg text-xs border transition"
                :class="
                  subType === t.id
                    ? 'bg-neutral-900 text-white border-neutral-900'
                    : 'bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-50'
                "
                @click="subType = t.id"
              >
                {{ t.name }}
                <span class="ml-1 opacity-70">{{ countOf(t.id) }}</span>
              </button>
            </div>

            <div v-if="subLoading" class="py-10 text-center text-sm text-neutral-400">
              <i class="fa fa-spinner fa-spin mr-1"></i>加载中…
            </div>

            <div v-else-if="filteredSubmissions.length" class="space-y-3">
              <div
                v-for="item in filteredSubmissions"
                :key="item.type + '-' + item.id"
                class="bg-white rounded-xl border border-neutral-200 p-3 sm:p-4 flex items-start sm:items-center gap-3"
              >
                <div
                  class="w-11 h-11 shrink-0 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden"
                >
                  <img v-if="item.cover" :src="item.cover" class="w-full h-full object-cover" alt="" />
                  <i v-else :class="item.icon" class="text-neutral-400"></i>
                </div>

                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span
                      class="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500 whitespace-nowrap"
                      >{{ item.typeName }}</span
                    >
                    <h3 class="font-bold text-neutral-900 text-sm truncate">
                      {{ item.title }}
                    </h3>
                    <span
                      class="text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap"
                      :class="subStatusClass(item)"
                      >{{ subStatusText(item) }}</span
                    >
                  </div>
                  <div class="text-xs text-neutral-500 mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap">
                    <span><i class="fa fa-clock-o mr-1"></i>{{ fmtSubTime(item.createTime) }}</span>
                    <span v-if="item.examInfo" class="text-red-500">
                      驳回原因：{{ item.examInfo }}
                    </span>
                  </div>
                </div>

                <div class="flex items-center gap-1 shrink-0">
                  <button
                    v-if="item.editable"
                    class="p-2 text-neutral-400 hover:text-primary transition-colors"
                    title="编辑后重新提交审核"
                    @click="editSubmission(item)"
                  >
                    <i class="fa fa-edit"></i>
                  </button>
                  <button
                    class="p-2 text-neutral-400 hover:text-red-500 transition-colors"
                    title="删除"
                    @click="removeSubmission(item)"
                  >
                    <i class="fa fa-trash-o"></i>
                  </button>
                </div>
              </div>
              <p class="text-xs text-neutral-400 text-right">
                每类最多显示最近 50 条
              </p>
            </div>

            <div v-else class="bg-white rounded-xl border border-neutral-200 p-12 text-center">
              <div
                class="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4 text-neutral-400"
              >
                <i class="fa fa-upload text-2xl"></i>
              </div>
              <h3 class="text-lg font-medium text-neutral-900 mb-2">暂无发布</h3>
              <p class="text-neutral-500 text-sm mb-6">
                发布工具、投稿案例或提出需求，都会显示在这里
              </p>
              <router-link
                to="/submit"
                class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                立即发布
              </router-link>
            </div>
          </div>

          <!-- 我的智能体 -->
          <MyAgentPanel v-else-if="currentTab === 'myagent'" />

          <!-- 我的知识链（知识体系 + 错题 → 知识图谱） -->
          <KnowledgeChainPanel v-else-if="currentTab === 'knowledgechain'" />

          <!-- 我的需求 -->
          <div v-else-if="currentTab === 'requests'" class="space-y-6">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-lg font-bold text-neutral-900">我的需求</h2>
              <router-link
                to="/request"
                class="text-sm text-primary hover:underline font-medium"
              >
                <i class="fa fa-plus mr-1"></i>提出新需求
              </router-link>
            </div>

            <div v-if="requestList.length > 0" class="space-y-4">
              <div
                v-for="request in requestList"
                :key="request.id"
                class="bg-white rounded-xl border border-neutral-200 p-4 hover-lift group relative"
              >
                <div class="flex justify-between items-start mb-2">
                  <h3
                    class="font-bold text-neutral-900 group-hover:text-primary transition-colors cursor-pointer flex-1 mr-4"
                    @click="openRequestDetail(request)"
                  >
                    {{ request.title }}
                  </h3>
                  <span
                    class="text-xs px-2 py-1 rounded-full whitespace-nowrap"
                    :class="{
                      'bg-yellow-100 text-yellow-700': request.status === 0,
                      'bg-green-100 text-green-700': request.status === 1,
                    }"
                  >
                    {{ getRequestStatusText(request.status) }}
                  </span>
                </div>
                <p class="text-sm text-neutral-500 line-clamp-2 mb-3 whitespace-pre-line" @click="openRequestDetail(request)">
                  {{ request.content }}
                </p>
                <div
                  class="flex items-center justify-between"
                >
                  <div class="flex items-center text-xs text-neutral-500 space-x-4">
                    <span
                      ><i class="fa fa-folder-o mr-1"></i
                      >{{
                        request.categoryName ||
                        getCategoryName(request.categoryId)
                      }}</span
                    >
                    <span
                      ><i class="fa fa-calendar mr-1"></i
                      >{{
                        new Date(request.createTime).format("yyyy-MM-dd")
                      }}</span
                    >
                    <span
                      ><i class="fa fa-comment-o mr-1"></i
                      >{{ request.replyCount || 0 }} 回复</span
                    >
                  </div>

                  <!-- 操作按钮区域 -->
                  <div class="flex items-center gap-2">
                    <!-- 标记解决按钮 - 仅进行中状态显示 -->
                    <button
                      v-if="request.status === 0"
                      @click.stop="resolveRequest(request)"
                      class="px-3 py-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-xs font-medium flex items-center"
                    >
                      <i class="fa fa-check mr-1"></i>标记解决
                    </button>
                    <!-- 重新打开按钮 - 仅已解决状态显示 -->
                    <button
                      v-if="request.status === 1"
                      @click.stop="reopenRequest(request)"
                      class="px-3 py-1.5 bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 transition-colors text-xs font-medium flex items-center"
                    >
                      <i class="fa fa-refresh mr-1"></i>重新打开
                    </button>
                    <!-- 查看详情按钮 -->
                    <button
                      @click.stop="openRequestDetail(request)"
                      class="px-3 py-1.5 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-xs font-medium flex items-center"
                    >
                      <i class="fa fa-eye mr-1"></i>详情
                    </button>
                    <!-- 删除按钮 -->
                    <button
                      @click.stop="deleteRequest(request)"
                      class="px-3 py-1.5 bg-neutral-50 text-neutral-600 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors text-xs font-medium flex items-center"
                    >
                      <i class="fa fa-trash-o mr-1"></i>删除
                    </button>
                  </div>
                </div>
              </div>

              <div class="flex justify-end w-full">
                <el-pagination
                  background
                  layout="prev, pager, next"
                  :total="requestTotal"
                  @current-change="pageChange($event, 'requests')"
                />
              </div>
            </div>

            <div
              v-else
              class="bg-white rounded-xl border border-neutral-200 p-12 text-center"
            >
              <div
                class="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4 text-neutral-400"
              >
                <i class="fa fa-lightbulb-o text-2xl"></i>
              </div>
              <h3 class="text-lg font-medium text-neutral-900 mb-2">
                暂无需求
              </h3>
              <p class="text-neutral-500 text-sm mb-6">
                提出您的工具需求，让社区为您推荐解决方案
              </p>
              <router-link
                to="/request"
                class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                立即提出需求
              </router-link>
            </div>
          </div>

          <!-- 创值记录 -->
          <div v-else-if="currentTab === 'points'" class="space-y-6">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-lg font-bold text-neutral-900">创值记录</h2>
              <div class="text-sm text-neutral-500">
                当前创值:
                <span class="text-primary font-bold">{{ user.points }}</span>
              </div>
            </div>

            <div
              class="bg-white rounded-xl border border-neutral-200 overflow-hidden"
            >
              <table class="w-full text-sm text-left">
                <thead
                  class="bg-neutral-50 text-neutral-500 border-b border-neutral-200"
                >
                  <tr>
                    <th class="px-6 py-3 font-medium">行为</th>
                    <th class="px-6 py-3 font-medium">变动</th>
                    <th class="px-6 py-3 font-medium text-right">时间</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-neutral-100">
                  <tr
                    v-for="(record, index) in pointRecords"
                    :key="index"
                    class="hover:bg-neutral-50"
                  >
                    <td class="px-6 py-4 text-neutral-900">
                      {{ getPointTitle(record.flowType, record.bizNote) }}
                    </td>
                    <td
                      class="px-6 py-4 font-medium"
                      :class="
                        record.integral > 0 ? 'text-green-600' : 'text-red-600'
                      "
                    >
                      {{ record.integral > 0 ? "+" : "" }}{{ record.integral }}
                    </td>
                    <td class="px-6 py-4 text-neutral-500 text-right">
                      {{
                        new Date(record.createTime).format("yyyy-MM-dd hh:mm")
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="flex justify-end w-full">
              <el-pagination
                background
                layout="prev, pager, next"
                :total="pointTotal"
                @current-change="pageChange($event, 'points')"
              />
            </div>
          </div>

          <!-- 账号设置 -->
          <div v-else-if="currentTab === 'settings'" class="max-w-2xl mx-auto">
            <h2 class="text-lg font-bold text-neutral-900 mb-6">账号设置</h2>

            <div
              class="bg-white rounded-xl border border-neutral-200 p-6 md:p-8 space-y-6"
            >
              <!-- 基本信息 -->
              <div>
                <h3
                  class="text-sm font-bold text-neutral-900 mb-4 border-b border-neutral-100 pb-2"
                >
                  基本信息
                </h3>
                <div class="space-y-4">
                  <div>
                    <label
                      class="block text-sm font-medium text-neutral-700 mb-1"
                      >头像</label
                    >
                    <div class="flex items-center space-x-4">
                      <img
                        :src="userInfo.avatar"
                        class="w-16 h-16 rounded-full object-cover border border-neutral-200"
                      />
                      <button
                        @click="triggerAvatarUpload"
                        class="px-4 py-2 border border-neutral-200 rounded-lg text-sm hover:bg-neutral-50 transition-colors"
                      >
                        更换头像
                      </button>
                    </div>
                  </div>

                  <div>
                    <label
                      class="block text-sm font-medium text-neutral-700 mb-1"
                      >昵称</label
                    >
                    <input
                      type="text"
                      v-model="userInfo.username"
                      class="w-full px-4 py-2 rounded-lg border border-neutral-200 focus:ring-2 focus:ring-primary/50 focus:border-primary focus:outline-none"
                    />
                  </div>

                  <div>
                    <label
                      class="block text-sm font-medium text-neutral-700 mb-1"
                      >邮箱</label
                    >
                    <input
                      type="email"
                      v-model="userInfo.email"
                      class="w-full px-4 py-2 rounded-lg border border-neutral-200 bg-neutral-50 text-neutral-500 cursor-not-allowed"
                      readonly
                    />
                  </div>

                  <div>
                    <label
                      class="block text-sm font-medium text-neutral-700 mb-1"
                      >个人简介</label
                    >
                    <textarea
                      v-model="userInfo.bio"
                      rows="3"
                      class="w-full px-4 py-2 rounded-lg border border-neutral-200 focus:ring-2 focus:ring-primary/50 focus:border-primary focus:outline-none"
                      placeholder="介绍一下你自己..."
                    ></textarea>
                  </div>
                </div>
              </div>

              <!-- 安全设置 -->
              <div>
                <h3
                  class="text-sm font-bold text-neutral-900 mb-4 border-b border-neutral-100 pb-2 pt-4"
                >
                  安全设置
                </h3>
                <div class="space-y-4">
                  <div>
                    <label
                      class="block text-sm font-medium text-neutral-700 mb-1"
                      >当前密码</label
                    >
                    <input
                      type="password"
                      v-model="userInfo.currentPassword"
                      class="w-full px-4 py-2 rounded-lg border border-neutral-200 focus:ring-2 focus:ring-primary/50 focus:border-primary focus:outline-none"
                      placeholder="如需修改密码请填写"
                    />
                  </div>
                  <div>
                    <label
                      class="block text-sm font-medium text-neutral-700 mb-1"
                      >新密码</label
                    >
                    <input
                      type="password"
                      v-model="userInfo.newPassword"
                      class="w-full px-4 py-2 rounded-lg border border-neutral-200 focus:ring-2 focus:ring-primary/50 focus:border-primary focus:outline-none"
                      placeholder="6-15位，需包含数字/字母/特殊字符至少2种"
                    />
                  </div>
                </div>
              </div>

              <div class="pt-4 border-t border-neutral-100 flex justify-end">
                <button
                  @click="saveSettings"
                  class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
                >
                  保存修改
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 收藏的提示词详情弹窗 -->
    <el-dialog
      v-model="promptFavDialog"
      :title="promptFavCurrent ? promptFavCurrent.title : ''"
      width="640px"
      top="8vh"
    >
      <div v-if="promptFavCurrent">
        <pre
          class="bg-neutral-50 border border-neutral-200 rounded-lg p-4 text-sm text-neutral-800 whitespace-pre-wrap break-words max-h-[55vh] overflow-y-auto"
          >{{ promptFavCurrent.content }}</pre
        >
      </div>
      <template #footer>
        <button
          class="px-5 py-2 rounded-md bg-primary text-white text-sm hover:bg-primary/90 transition-colors"
          @click="copyPromptFav"
        >
          <i class="fa" :class="promptFavCopied ? 'fa-check' : 'fa-copy'"></i>
          {{ promptFavCopied ? "已复制" : "复制内容" }}
        </button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { tools } from "@/data/mock";
import { ElMessageBox, ElMessage } from "element-plus";
import { globalState } from "@/state";
import { requireLogin } from "@/utils/authGuard";
import { imageMaxBytes, getUploadConfig } from "@/utils/uploadConfig";
import { normalizeSiteUrl } from "@/utils/siteLink";
import MyAgentPanel from "./user/components/MyAgentPanel.vue";
import KnowledgeChainPanel from "./user/components/KnowledgeChainPanel.vue";

export default {
  name: "UserCenterPage",
  components: { MyAgentPanel, KnowledgeChainPanel },
  data() {
    return {
      tools: tools,
      currentTab: "mine",
      /** 侧栏「应用」分组是否展开（默认展开） */
      appOpen: true,
      unreadTotal: 0,
      /** 侧栏分组：我的内容 / 账号与记录（应用、创作单独渲染） */
      tabGroups: [
        { id: "my", name: "我的内容" },
        { id: "account", name: "账号与记录" },
      ],
      tabs: [
        { id: "submissions", name: "我的发布", icon: "fa-cube", group: "my" },
        { id: "requests", name: "我的需求", icon: "fa-lightbulb-o", group: "my" },
        { id: "myagent", name: "我的智能体", icon: "fa-robot", group: "my" },
        { id: "knowledgechain", name: "我的知识链", icon: "fa-share-alt", group: "my" },
        { id: "points", name: "创值记录", icon: "fa-history", group: "account" },
        { id: "settings", name: "账号设置", icon: "fa-cog", group: "account" },
      ],
      /** 创作：四个发布入口（与创想广场一致，个人板块内直接展示） */
      publishEntries: [
        {
          to: "/submit",
          label: "发布工具",
          desc: "把你的 AI 应用上架到应用广场",
          icon: "fa-plus-circle",
          bg: "bg-primary",
        },
        {
          to: "/request",
          label: "发布需求",
          desc: "说说你希望有人做出什么工具",
          icon: "fa-question-circle-o",
          bg: "bg-amber-500",
        },
        {
          to: "/cases/submit",
          label: "投稿案例",
          desc: "分享 AI 在课堂里的真实用法",
          icon: "fa-file-text-o",
          bg: "bg-emerald-500",
        },
        {
          to: "/prompts",
          label: "分享提示词",
          desc: "沉淀好用的提示词与技巧",
          icon: "fa-bookmark-o",
          bg: "bg-purple-500",
        },
      ],
      /** 我的应用：类型筛选（平铺展示，不做收藏文案） */
      mineFilter: "all",
      mineFilters: [
        { id: "all", name: "全部", icon: "fa-th-large" },
        { id: "tool", name: "AI工具", icon: "fa-cube" },
        { id: "agent", name: "智能体", icon: "fa-robot" },
        { id: "case", name: "案例", icon: "fa-graduation-cap" },
        { id: "prompt", name: "提示词", icon: "fa-bookmark" },
        { id: "web", name: "网站", icon: "fa-window-maximize" },
        { id: "software", name: "软件", icon: "fa-download" },
        { id: "request", name: "需求", icon: "fa-lightbulb-o" },
      ],
      mineList: [],
      mineTotal: 0,
      minePage: 1,
      minePageSize: 24,
      mineLoading: false,
      /** 我的作品（工具）及其收藏/点赞/下载/浏览 */
      works: [],
      worksLoading: false,
      /** 个人首页设置（打开站点直达的内容） */
      homePref: null,
      mineCounts: {
        tool: 0,
        agent: 0,
        case: 0,
        prompt: 0,
        web: 0,
        software: 0,
        request: 0,
      },
      /** 案例收藏 */
      caseFavList: [],
      caseFavTotal: 0,
      caseFavPage: 1,
      /** 智能体收藏 */
      agentFavList: [],
      agentFavTotal: 0,
      agentFavPage: 1,
      /** 是否有后台权限（决定是否显示「管理后台」入口） */
      isAdmin: false,
      adminPerms: "",
      /** 个人数据大屏 */
      dashLoading: false,
      dash: {
        integral: 0,
        fav: { tool: 0, agent: 0, case: 0, prompt: 0 },
        create: { tool: 0, case: 0, demand: 0, prompt: 0 },
      },
      pointRecords: [
        { action: "每日签到", change: 5, date: "2023-12-25 09:00" },
        {
          action: '发布工具 "AI 写作助手"',
          change: 50,
          date: "2023-12-24 14:30",
        },
        { action: "评论工具", change: 2, date: "2023-12-23 11:20" },
        { action: "下载工具", change: -5, date: "2023-12-22 16:45" },
        { action: "完善个人信息", change: 20, date: "2023-12-20 10:00" },
      ],
      myToolList: [],
      sendPage: 1,
      sendTotal: 0,
      requestPage: 1,
      requestTotal: 0,
      requestList: [],

      pointPage: 1,
      pointTotal: 0,
      favPage: 1,
      favTotal: 0,
      favToolList: [],
      promptFavPage: 1,
      promptFavTotal: 0,
      promptFavList: [],
      promptFavDialog: false,
      promptFavCurrent: null,
      promptFavCopied: false,
      levelInfo: {
        level: 1,
        levelName: "AI爱好者",
        totalEarned: 0,
        nextNeed: 100,
        bio: "",
        // 算力阶序：智点（升级凭证，永久累计）
        wisdom: 0,
        // 算力阶序：创值（可兑奖代币，支持充值）
        credit: 0,
        // 智点赛道（科技蓝）
        devLevel: 1,
        devLevelName: "硅基学徒",
        devToolCount: 0,
        devNext: 40,
        devTooltip: "初入集市，开启智点积累",
        // 创作赛道（活力橙）：按累计作品数量
        userLevel: 1,
        userLevelName: "AI爱好者",
        userToolCount: 0,
        userMinutes: 0,
        userNext: 1,
        userTooltip: "开始你的第一次创作",
        // 附加身份徽章
        badges: "",
        // 兑换窗口（周六日开放）
        exchangeOpen: false,
      },
      bioDialogVisible: false,
      bioEditing: false,
      bioText: "",
      bioSaving: false,
      userInfo: {
        avatar: "",
        username: "",
        email: "",
        bio: "",
        currentPassword: "",
        newPassword: "",
      },
      categoryList:[],
      // 我的发布：工具/案例/需求/提示词 聚合
      subType: "all",
      subLoading: false,
      subList: [],
      subTypes: [
        { id: "all", name: "全部" },
        { id: "tool", name: "工具" },
        { id: "case", name: "案例" },
        { id: "demand", name: "需求" },
        { id: "prompt", name: "技巧" },
      ],
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    isLogin() {
      return this.user && this.user.uid;
    },
    /** 数据大屏：收藏总数 */
    dashFavTotal() {
      const f = this.dash.fav;
      return f.tool + f.agent + f.case + f.prompt;
    },
    /** 数据大屏：创作总数 */
    dashCreateTotal() {
      const c = this.dash.create;
      return c.tool + c.case + c.demand + c.prompt;
    },
    /** 我的应用总数（全部类型） */
    mineTotalAll() {
      return Object.values(this.mineCounts).reduce(
        (sum, n) => sum + (Number(n) || 0),
        0,
      );
    },
    /** 当前应用筛选的标题 */
    mineFilterName() {
      const f = this.mineFilters.find((x) => x.id === this.mineFilter);
      return this.mineFilter === "all" || !f
        ? "我的应用"
        : "我的应用 · " + f.name;
    },
    favBars() {
      const f = this.dash.fav;
      const max = Math.max(f.tool, f.agent, f.case, f.prompt, 1);
      return [
        { label: "AI工具", value: f.tool, pct: (f.tool / max) * 100, color: "bg-blue-500" },
        { label: "智能体", value: f.agent, pct: (f.agent / max) * 100, color: "bg-indigo-500" },
        { label: "案例", value: f.case, pct: (f.case / max) * 100, color: "bg-emerald-500" },
        { label: "提示词", value: f.prompt, pct: (f.prompt / max) * 100, color: "bg-purple-500" },
      ];
    },
    createBars() {
      const c = this.dash.create;
      const max = Math.max(c.tool, c.case, c.demand, c.prompt, 1);
      return [
        { label: "工具", value: c.tool, pct: (c.tool / max) * 100, color: "bg-blue-500" },
        { label: "案例", value: c.case, pct: (c.case / max) * 100, color: "bg-emerald-500" },
        { label: "需求", value: c.demand, pct: (c.demand / max) * 100, color: "bg-amber-500" },
        { label: "提示词", value: c.prompt, pct: (c.prompt / max) * 100, color: "bg-purple-500" },
      ];
    },
    filteredSubmissions() {
      if (this.subType === "all") return this.subList;
      return this.subList.filter((x) => x.type === this.subType);
    },
  },
  watch: {
    // 监听登录状态变化，登录状态改变时重新查询创值
    isLogin(newVal) {
      if (newVal) {
        this.fetchUserPoints();
      }
    },
  },
  mounted() {
    // 组件挂载时，如果已登录则查询创值
    if (this.isLogin) {
      this.fetchUserPoints();
    }
    this.fetchUnread();
  },
  async created() {
    // 访客模式：个人中心需要登录
    if (!requireLogin(this, "个人中心")) return;
    await this.initData();
    this.getCategories();
    this.loadAdminPerms();
    this.loadDashboard();
    this.loadMine();
    this.loadMyWorks();
    this.loadHomePref();
  },
  methods: {
    /** 侧栏分组：按 group 过滤 tabs */
    tabsByGroup(group) {
      return this.tabs.filter((t) => t.group === group);
    },
    /** 个人数据大屏：创值 / 收藏 / 创作分布 */
    async loadDashboard() {
      this.dashLoading = true;
      try {
        const enc = (o) => this.$aesEncrypt(o);
        const p1 = await enc({ page: 1, pageSize: 1 });
        const p1Case = await enc({ page: 1, pageSize: 1, status: -1 });
        const [integral, toolFav, promptFav, caseIds, agentIds, myTools, myCases, myDemands, myPrompts] =
          await Promise.allSettled([
            this.$axios({ url: "user/integral" }),
            this.$axios({
              url: "aitool/user/list/fav",
              method: "POST",
              data: { data: p1 },
            }),
            this.$axios({
              url: "prompt/fav/list",
              method: "POST",
              data: { data: p1 },
            }),
            this.$axios({ url: "fav/ids/case" }),
            this.$axios({ url: "fav/ids/agent" }),
            this.$axios({
              url: "aitool/list/mine",
              method: "POST",
              data: { data: p1 },
            }),
            this.$axios({
              url: "case/mine",
              method: "POST",
              data: { data: p1Case },
            }),
            this.$axios({
              url: "demand/mine",
              method: "POST",
              data: { data: p1 },
            }),
            this.$axios({ url: "prompt/mylist", method: "POST" }),
          ]);
        // 统一从 {total,list} 或数组里取数量
        const num = (r) => {
          if (!r || r.status !== "fulfilled" || !r.value || r.value.code !== 200) {
            return 0;
          }
          const d = r.value.data;
          if (Array.isArray(d)) return d.length;
          if (d && typeof d === "object") {
            if (typeof d.total === "number") return d.total;
            if (Array.isArray(d.list)) return d.list.length;
          }
          return 0;
        };
        this.dash.integral =
          integral.status === "fulfilled" &&
          integral.value &&
          integral.value.code === 200 &&
          integral.value.data
            ? integral.value.data.point || 0
            : 0;
        this.dash.fav = {
          tool: num(toolFav),
          agent: num(agentIds),
          case: num(caseIds),
          prompt: num(promptFav),
        };
        this.dash.create = {
          tool: num(myTools),
          case: num(myCases),
          demand: num(myDemands),
          prompt: num(myPrompts),
        };
        // 我的应用分类数量（含网站/软件/需求）
        this.$axios({ url: "fav/mine/counts" })
          .then((r) => {
            if (r.code === 200 && r.data && r.data.counts) {
              this.mineCounts = { ...this.mineCounts, ...r.data.counts };
            }
          })
          .catch(() => {});
      } catch (e) {
        /* 单项失败不影响其他数据 */
      } finally {
        this.dashLoading = false;
      }
    },
    /** 有管理权限时，个人中心直接显示后台入口 */
    async loadAdminPerms() {
      if (!localStorage.getItem("token")) return;
      try {
        const res = await this.$axios({ url: "user/admin/perms", method: "POST" });
        if (res.code === 200 && res.data) {
          this.adminPerms = res.data.perms || "";
          this.isAdmin =
            res.data.utype === "admin" ||
            (typeof this.adminPerms === "string" && this.adminPerms.length > 0);
        }
      } catch (e) {
        /* 非管理员或无权限时静默 */
      }
    },
    async loadCaseFav() {
      const str = await this.$aesEncrypt({
        page: this.caseFavPage,
        pageSize: 9,
      });
      this.$axios({
        url: "fav/list/case",
        method: "POST",
        data: { data: str },
      }).then((res) => {
        if (res.code == 200) {
          this.caseFavTotal = res.data.total;
          this.caseFavList = res.data.list || [];
        }
      });
    },
    async loadAgentFav() {
      const str = await this.$aesEncrypt({
        page: this.agentFavPage,
        pageSize: 9,
      });
      this.$axios({
        url: "fav/list/agent",
        method: "POST",
        data: { data: str },
      }).then((res) => {
        if (res.code == 200) {
          this.agentFavTotal = res.data.total;
          this.agentFavList = res.data.list || [];
        }
      });
    },
    removeCaseFav(item) {
      this.$axios({ url: "fav/case/" + item.id, method: "DELETE" }).then(
        (res) => {
          if (res.code == 200) {
            ElMessage.success("已取消收藏");
            this.loadCaseFav();
          } else {
            ElMessage.error(res.msg || "操作失败");
          }
        },
      );
    },
    removeAgentFav(item) {
      this.$axios({ url: "fav/agent/" + item.id, method: "DELETE" }).then(
        (res) => {
          if (res.code == 200) {
            ElMessage.success("已取消收藏");
            this.loadAgentFav();
          } else {
            ElMessage.error(res.msg || "操作失败");
          }
        },
      );
    },
    goCase() {
      this.$router.push("/cases");
    },
    goAgent(item) {
      this.$router.push("/agent/" + item.id);
    },
    agentFirstChar(name) {
      return name ? name.trim().charAt(0) : "A";
    },
    // 消息中心未读数
    fetchUnread() {
      this.$axios({ url: "notice/unread", method: "GET" })
        .then((res) => {
          if (res.code === 200 && res.data) {
            this.unreadTotal = res.data.total || 0;
          }
        })
        .catch(() => {});
    },
    goMessages() {
      this.$router.push("/messages");
    },
    async getCategories() {
      try {
        const res = await this.$axios({
          url: "category/list",
        });
        if (res.code === 200) {
          this.categoryList = res.data || [];
        }
      } catch (error) {
        console.error("获取分类失败:", error);
      }
    },
    getStatusText(status) {
      const map = {
        0: "待审批",
        1: "已上架",
        2: "已拒绝",
      };
      return map[status] || "未知状态";
    },
    getRequestStatusText(status) {
      const map = {
        0: "进行中",
        1: "已解决",
      };
      return map[status] || "未知状态";
    },
    getCategoryName(id) {
      const category = this.categoryList.find((c) => c.id === id);
      return category ? category.title : "未知分类";
    },
    openRequestDetail(request) {
      globalState.openRequestModal(request.id, this.categoryList);
    },

    checkLogin(router) {
      if (!this.isLogin) {
        ElMessageBox.confirm("需要使用当前功能，请先登录账号", "登录", {
          confirmButtonText: "登录",
          cancelButtonText: "取消",
          type: "warning",
        })
          .then(() => {
            this.$router.push("/login");
          })
          .catch(() => {});
        return;
      }

      this.$axios({
        url: "aitool/user/checklogin",
      }).then((res) => {
        if (res.code == 200) {
          this.$router.push(router);
        }
      });
    },
    async initData() {
      var favStr = await this.$aesEncrypt({
        page: this.favPage,
        pageSize: 10,
      });
      this.$axios({
        url: "aitool/user/list/fav",
        method: "POST",
        data: {
          data: favStr,
        },
      }).then((res) => {
        if (res.code == 200) {
          this.favTotal = res.data.total;
          this.favToolList = res.data.list;
        }
      });
      // 提示词收藏数量（用于顶部统计）
      this.$axios({
        url: "prompt/fav/list",
        method: "POST",
        data: {
          data: await this.$aesEncrypt({ page: 1, pageSize: 1 }),
        },
      })
        .then((res) => {
          if (res.code == 200 && res.data) {
            this.promptFavTotal = res.data.total;
          }
        })
        .catch(() => {});
    },
    fetchUserPoints() {
      this.$axios({
        url: "user/integral",
      }).then((res) => {
        if (res.code == 200) {
          var points = res.data.point;
          this.$store.commit("setPoints", points);
        }
      });
      // 等级 + 个人介绍（等级按累计获得创值计算）
      this.$axios({
        url: "user/level",
      }).then((res) => {
        if (res.code == 200 && res.data) {
          // 注意：这里必须接收接口返回的完整字段。
          // 之前只取了 5 个旧字段，导致 devLevel / userLevel / wisdom 等被清空，
          // 昵称旁的等级徽章渲染成空白。
          this.levelInfo = {
            level: res.data.level || 1,
            levelName: res.data.levelName || "AI爱好者",
            totalEarned: res.data.totalEarned || 0,
            nextNeed: res.data.nextNeed || 0,
            bio: res.data.bio || "",
            // 智点（升级凭证，永久累计）与创值（可充值代币）
            wisdom: res.data.wisdom || 0,
            credit: res.data.credit || 0,
            // 智点赛道（科技蓝）
            devLevel: res.data.devLevel || 1,
            devLevelName: res.data.devLevelName || "硅基学徒",
            devToolCount: res.data.devToolCount || 0,
            devNext: res.data.devNext || 0,
            devTooltip: res.data.devTooltip || "",
            // 创作赛道（活力橙）：按累计作品数量
            userLevel: res.data.userLevel || 1,
            userLevelName: res.data.userLevelName || "AI爱好者",
            userToolCount: res.data.userToolCount || 0,
            userMinutes: res.data.userMinutes || 0,
            userNext: res.data.userNext || 0,
            userTooltip: res.data.userTooltip || "",
            // 附加身份徽章（逗号分隔）
            badges: res.data.badges || "",
            exchangeOpen: !!res.data.exchangeOpen,
          };
        }
      });
    },
    /** 附加身份徽章列表（后端按统计自动授予，逗号分隔） */
    badgeList() {
      return (this.levelInfo.badges || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    },
    openBioDialog() {
      this.bioDialogVisible = true;
      this.bioEditing = false;
      this.bioText = this.levelInfo.bio || "";
    },
    async saveBio() {
      if (this.bioSaving) return;
      this.bioSaving = true;
      try {
        const dataStr = await this.$aesEncrypt({
          uid: this.user.uid,
          bio: this.bioText,
        });
        const res = await this.$axios({
          url: "user/update/bio",
          method: "POST",
          data: { data: dataStr },
        });
        if (res.code == 200) {
          this.levelInfo.bio = this.bioText.trim();
          this.bioEditing = false;
          ElMessage.success("个人介绍已更新");
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (e) {
        ElMessage.error("保存失败，请稍后重试");
      } finally {
        this.bioSaving = false;
      }
    },
    logout() {
      if (confirm("确定要退出登录吗？")) {
        // this.state.logout();
        localStorage.clear();
        this.$store.commit("clearUserInfo");
        this.$router.push("/");
      }
    },
    async deleteAccount() {
      const confirmed1 = confirm(
        "注销账号将删除您的个人信息（昵称、头像、手机号等）及创值、收藏、点赞、评论记录，且无法恢复。\n确定要继续吗？"
      );
      if (!confirmed1) return;
      const confirmed2 = confirm("再次确认：注销操作不可撤销，您发布的内容将显示为“已注销用户”。\n确定注销吗？");
      if (!confirmed2) return;
      try {
        const res = await this.$axios({ url: "user/user", method: "DELETE" });
        if (res.code == 200) {
          localStorage.clear();
          this.$store.commit("clearUserInfo");
          this.$router.push("/");
          alert("账号已注销，感谢您的使用。");
        } else {
          alert(res.msg || "注销失败，请稍后重试");
        }
      } catch (e) {
        alert("注销失败，请稍后重试");
      }
    },
    getPointTitle(type, bizNote) {
      if (type == 1) {
        return "每日签到";
      } else if (type == 2) {
        return "下载工具";
      } else if (type == 3) {
        return "销售工具";
      } else if (type == 4) {
        return "发布工具"
      }
      return bizNote;
    },
    /* ---------- 我的发布：工具 / 案例 / 需求 / 提示词 ---------- */
    async loadMySubmissions() {
      this.subLoading = true;
      try {
        const enc = (o) => this.$aesEncrypt(o);
        const [tools, cases, demands, prompts] = await Promise.allSettled([
          this.$axios({
            url: "aitool/list/mine",
            method: "POST",
            data: { data: await enc({ page: 1, pageSize: 50 }) },
          }),
          this.$axios({
            url: "case/mine",
            method: "POST",
            data: { data: await enc({ page: 1, pageSize: 50, status: -1 }) },
          }),
          this.$axios({
            url: "demand/mine",
            method: "POST",
            data: { data: await enc({ page: 1, pageSize: 50 }) },
          }),
          this.$axios({ url: "prompt/mylist", method: "POST" }),
        ]);

        const pick = (r) =>
          r.status === "fulfilled" && r.value && r.value.code === 200 && r.value.data
            ? r.value.data
            : null;

        const list = [];
        const toolData = pick(tools);
        if (toolData && Array.isArray(toolData.list)) {
          toolData.list.forEach((t) =>
            list.push({
              type: "tool",
              typeName: "工具",
              icon: "fa fa-cube",
              id: t.id,
              title: t.title || "未命名工具",
              cover: t.cover || "",
              status: t.status,
              examInfo: t.examinfo || t.examInfo || "",
              createTime: t.createTime,
              editable: true,
              raw: t,
            })
          );
        }
        const caseData = pick(cases);
        if (caseData && Array.isArray(caseData.list)) {
          caseData.list.forEach((c) =>
            list.push({
              type: "case",
              typeName: "案例",
              icon: "fa fa-file-text-o",
              id: c.id,
              title: c.title || "未命名案例",
              cover: "",
              status: c.status,
              examInfo: c.examInfo || "",
              createTime: c.createTime,
              editable: true,
              raw: c,
            })
          );
        }
        const demandData = pick(demands);
        if (demandData && Array.isArray(demandData.list)) {
          demandData.list.forEach((d) =>
            list.push({
              type: "demand",
              typeName: "需求",
              icon: "fa fa-lightbulb-o",
              id: d.id,
              title: d.title || "未命名需求",
              cover: "",
              status: d.status,
              examInfo: "",
              createTime: d.createTime,
              editable: true,
              raw: d,
            })
          );
        }
        const promptData = pick(prompts);
        if (promptData && Array.isArray(promptData.list)) {
          promptData.list.forEach((p) =>
            list.push({
              type: "prompt",
              typeName: "技巧",
              icon: "fa fa-bookmark-o",
              id: p.id,
              title: p.title || "未命名分享",
              cover: "",
              status: p.status,
              examInfo: p.examInfo || "",
              createTime: p.createTime,
              editable: false,
              raw: p,
            })
          );
        }

        list.sort((a, b) => this.subTimeVal(b.createTime) - this.subTimeVal(a.createTime));
        this.subList = list;
      } finally {
        this.subLoading = false;
      }
    },
    countOf(type) {
      if (type === "all") return this.subList.length;
      return this.subList.filter((x) => x.type === type).length;
    },
    subTimeVal(t) {
      if (!t) return 0;
      if (Array.isArray(t)) {
        return new Date(
          t[0],
          (t[1] || 1) - 1,
          t[2] || 1,
          t[3] || 0,
          t[4] || 0,
          t[5] || 0
        ).getTime();
      }
      const v = new Date(String(t).replace(" ", "T")).getTime();
      return isNaN(v) ? 0 : v;
    },
    fmtSubTime(t) {
      if (!t) return "";
      if (Array.isArray(t)) {
        const p = (n) => String(n || 0).padStart(2, "0");
        return `${t[0]}-${p(t[1])}-${p(t[2])} ${p(t[3])}:${p(t[4])}`;
      }
      return String(t).replace("T", " ").substring(0, 16);
    },
    subStatusText(item) {
      if (item.type === "demand") return Number(item.status) === 1 ? "已解决" : "进行中";
      const map = { 0: "待审核", 1: "已通过", 2: "已驳回" };
      return map[Number(item.status)] || "—";
    },
    subStatusClass(item) {
      if (item.type === "demand") {
        return Number(item.status) === 1
          ? "bg-green-100 text-green-700"
          : "bg-sky-100 text-sky-700";
      }
      const s = Number(item.status);
      if (s === 1) return "bg-green-100 text-green-700";
      if (s === 2) return "bg-red-100 text-red-700";
      return "bg-yellow-100 text-yellow-700";
    },
    editSubmission(item) {
      if (item.type === "tool") {
        this.$router.push({ path: "/submit", query: { id: item.id } });
      } else if (item.type === "case") {
        this.$router.push({ path: "/cases/submit", query: { id: item.id } });
      } else if (item.type === "demand") {
        this.$router.push({ path: "/request", query: { id: item.id } });
      }
    },
    removeSubmission(item) {
      const url =
        item.type === "tool"
          ? "aitool/" + item.id
          : item.type === "case"
          ? "case/" + item.id
          : item.type === "demand"
          ? "demand/" + item.id
          : "prompt/mine/" + item.id;
      ElMessageBox.confirm(`确定删除「${item.title}」吗？删除后不可恢复`, "删除", {
        confirmButtonText: "删除",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          this.$axios({ url, method: "DELETE" }).then((res) => {
            if (res.code === 200) {
              ElMessage.success("已删除");
              this.loadMySubmissions();
            } else {
              ElMessage.error(res.msg || "删除失败");
            }
          });
        })
        .catch(() => {});
    },

    /** 应用分组：展开/收起；从其他板块点「应用」时回到应用列表 */
    toggleAppGroup() {
      this.appOpen = !this.appOpen;
      if (this.currentTab !== "mine") {
        this.currentTab = "mine";
        this.mineFilter = "all";
        this.minePage = 1;
        this.loadMine();
      }
    },
    // ===== 我的应用（应用广场点「＋」加入的内容，平铺展示） =====
    async loadMine() {
      this.mineLoading = true;
      try {
        const data = await this.$aesEncrypt({
          page: this.minePage,
          pageSize: this.minePageSize,
          type: this.mineFilter,
        });
        const res = await this.$axios({
          url: "fav/mine",
          method: "POST",
          data: { data },
        });
        if (res.code === 200 && res.data) {
          this.mineList = res.data.list || [];
          this.mineTotal = res.data.total || 0;
          if (res.data.counts) {
            this.mineCounts = { ...this.mineCounts, ...res.data.counts };
          }
        }
      } catch (e) {
        /* 未登录或接口异常时静默 */
      } finally {
        this.mineLoading = false;
      }
    },
    /** 切换应用类型筛选 */
    selectMineTab(filter) {
      this.currentTab = "mine";
      this.mineFilter = filter;
      this.minePage = 1;
      this.loadMine();
    },
    minePageChange(page) {
      this.minePage = page;
      this.loadMine();
    },
    mineTypeIcon(type) {
      const map = {
        tool: "fa-cube",
        agent: "fa-robot",
        case: "fa-graduation-cap",
        prompt: "fa-bookmark",
        web: "fa-window-maximize",
        software: "fa-download",
        request: "fa-lightbulb-o",
      };
      return map[type] || "fa-th-large";
    },
    /** 打开我的应用里的内容 */
    openMineItem(it) {
      if (!it) return;
      if (it.external) {
        const href = normalizeSiteUrl(it.link);
        if (href) window.open(href, "_blank", "noopener");
        else ElMessage.warning("站点地址无效，请联系管理员");
        return;
      }
      if (it.type === "tool") {
        this.$router.push("/tool/" + it.id);
      } else if (it.type === "agent") {
        this.$router.push("/agent/" + it.id);
      } else if (it.type === "case") {
        this.$router.push("/cases");
      } else if (it.type === "prompt") {
        this.$router.push("/prompts");
      } else if (it.type === "software") {
        this.$router.push("/software");
      } else if (it.type === "request") {
        this.$router.push("/requests");
      } else if (it.type === "web") {
        const href = normalizeSiteUrl(it.link);
        if (href) window.open(href, "_blank", "noopener");
        else ElMessage.warning("站点地址无效，请联系管理员");
      }
    },
    /** 移出我的应用 */
    removeMineItem(it) {
      if (!it) return;
      this.$axios({ url: `fav/${it.type}/${it.id}`, method: "DELETE" })
        .then((res) => {
          if (res.code === 200) {
            ElMessage.success("已从我的应用移出");
            this.loadMine();
          } else {
            ElMessage.error(res.msg || "操作失败");
          }
        })
        .catch(() => ElMessage.error("操作失败，请稍后重试"));
    },
    // ===== 个人首页（打开网站直接进入指定内容） =====
    async loadHomePref() {
      try {
        const res = await this.$axios({ url: "user/home/pref" });
        if (res.code === 200 && res.data) {
          this.homePref = res.data.pref || null;
        }
      } catch (e) {
        /* 静默 */
      }
    },
    isHomePref(it) {
      return (
        !!this.homePref &&
        this.homePref.type === it.type &&
        this.homePref.id === it.id
      );
    },
    async setHomePref(it) {
      const link = this.mineItemLink(it);
      if (!link) {
        ElMessage.warning("该内容暂不支持设为首页");
        return;
      }
      try {
        const data = await this.$aesEncrypt({
          contentType: it.type,
          contentId: it.id,
          title: it.title,
          cover: it.cover,
          link,
          external: !!it.external,
        });
        const res = await this.$axios({
          url: "user/home/pref",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          this.homePref = {
            type: it.type,
            id: it.id,
            title: it.title,
            cover: it.cover,
            link,
            external: !!it.external,
          };
          ElMessage.success("已设为首页，下次打开网站会直接进入");
        } else {
          ElMessage.error(res.msg || "设置失败");
        }
      } catch (e) {
        ElMessage.error("设置失败，请稍后重试");
      }
    },
    async cancelHomePref() {
      try {
        const res = await this.$axios({
          url: "user/home/pref",
          method: "DELETE",
        });
        if (res.code === 200) {
          this.homePref = null;
          ElMessage.success("已取消个人首页");
        }
      } catch (e) {
        ElMessage.error("取消失败，请稍后重试");
      }
    },
    /** 我的应用条目 -> 可跳转地址 */
    mineItemLink(it) {
      if (it.external && it.link) return it.link;
      if (it.link && it.link.startsWith("/")) return it.link;
      const map = {
        tool: "/tool/",
        agent: "/agent/",
        case: "/cases",
        prompt: "/prompts",
        software: "/software",
        request: "/requests",
      };
      const base = map[it.type];
      if (!base) return "";
      return base.endsWith("/") ? base + it.id : base;
    },

    /** 我的作品：收藏 / 点赞 / 下载 / 浏览 */
    async loadMyWorks() {
      this.worksLoading = true;
      try {
        const data = await this.$aesEncrypt({ page: 1, pageSize: 20 });
        const res = await this.$axios({
          url: "aitool/user/mine/stats",
          method: "POST",
          data: { data },
        });
        if (res.code === 200 && res.data) {
          this.works = res.data.list || [];
        }
      } catch (e) {
        /* 静默 */
      } finally {
        this.worksLoading = false;
      }
    },
    openWork(w) {
      if (!w || !w.id) return;
      this.$router.push("/tool/" + w.id);
    },

    /** 创作入口：在个人板块内展示发布入口（不跳转创想广场） */
    selectCreateTab() {
      this.currentTab = "create";
    },
    /** 跳转到对应的发布页面 */
    goPublish(entry) {
      if (!requireLogin(this, "发布内容")) return;
      this.$router.push(entry.to);
    },

    async selectTab(id) {
      this.currentTab = id;
      if (id === "casefav") {
        this.loadCaseFav();
        return;
      }
      if (id === "agentfav") {
        this.loadAgentFav();
        return;
      }
      if (this.currentTab === "submissions") {
        this.loadMySubmissions();
      } else if (this.currentTab === "points") {
        var dataStr = await this.$aesEncrypt({
          page: this.pointPage,
          pageSize: 10,
        });
        this.$axios({
          url: "user/integral/record",
          method: "POST",
          data: {
            data: dataStr,
          },
        }).then((res) => {
          this.pointRecords = res.data.list;
          this.pointTotal = res.data.total;
        });
      } else if (this.currentTab === "favorites") {
        var favStr = await this.$aesEncrypt({
          page: this.favPage,
          pageSize: 10,
        });
        this.$axios({
          url: "aitool/user/list/fav",
          method: "POST",
          data: {
            data: favStr,
          },
        }).then((res) => {
          this.favTotal = res.data.total;
          this.favToolList = res.data.list;
        });
      } else if (this.currentTab === "settings") {
        this.initUserInfo();
      } else if (this.currentTab === "promptfav") {
        var pfStr = await this.$aesEncrypt({
          page: this.promptFavPage,
          pageSize: 10,
        });
        const pfRes = await this.$axios({
          url: "prompt/fav/list",
          method: "POST",
          data: {
            data: pfStr,
          },
        });
        if (pfRes.code === 200 && pfRes.data) {
          this.promptFavTotal = pfRes.data.total;
          this.promptFavList = pfRes.data.list;
        }
      } else if (this.currentTab === "requests") {
        var reqStr = await this.$aesEncrypt({
          page: this.requestPage,
          pageSize: 10,
        });
        this.$axios({
          url: "demand/mine",
          method: "POST",
          data: {
            data: reqStr,
          },
        }).then((res) => {
          this.requestTotal = res.data.total;
          this.requestList = res.data.list;
        });
      }
    },
    // 打开收藏的提示词
    openPromptFav(item) {
      this.promptFavCurrent = item;
      this.promptFavCopied = false;
      this.promptFavDialog = true;
    },
    // 取消收藏（提示词）
    cancelPromptFav(item) {
      this.$axios({
        url: "prompt/fav/" + item.id,
        method: "DELETE",
      }).then((res) => {
        if (res.code === 200) {
          ElMessage.success("已取消收藏");
          if (
            this.promptFavDialog &&
            this.promptFavCurrent &&
            this.promptFavCurrent.id === item.id
          ) {
            this.promptFavDialog = false;
          }
          this.selectTab("promptfav");
        } else {
          ElMessage.error(res.msg || "操作失败");
        }
      });
    },
    copyPromptFav() {
      if (!this.promptFavCurrent) return;
      const text = this.promptFavCurrent.content || "";
      const done = () => {
        this.promptFavCopied = true;
        ElMessage.success("已复制到剪贴板");
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => done());
      } else {
        done();
      }
    },
    pageChange(page, type) {
      if (this.currentTab === "submissions") {
        this.sendPage = page;
      } else if (this.currentTab === "points") {
        this.pointPage = page;
      } else if (this.currentTab === "favorites") {
        this.favPage = page;
      } else if (this.currentTab === "promptfav") {
        this.promptFavPage = page;
      } else if (this.currentTab === "requests") {
        this.requestPage = page;
      } else if (this.currentTab === "casefav") {
        this.caseFavPage = page;
      } else if (this.currentTab === "agentfav") {
        this.agentFavPage = page;
      }

      this.selectTab(type);
    },
    deleteTool(tool) {
      ElMessageBox.confirm(
        `确定删除当前工具:${tool.title}么？删除后不可以恢复`,
        "删除工具",
        {
          confirmButtonText: "删除",
          cancelButtonText: "取消",
          type: "warning",
        },
      )
        .then(() => {
          this.$axios({
            url: "aitool/" + tool.id,
            method: "DELETE",
          }).then(async (res) => {
            if (res.code == 200) {
              ElMessage({
                message: "删除成功",
                type: "success",
              });
              await this.selectTab("submissions");
            } else {
              ElMessage({
                message: res.msh,
                type: "error",
              });
            }
          });
        })
        .catch(() => {});
    },

    // 删除需求
    deleteRequest(request) {
      ElMessageBox.confirm(
        `确定删除需求"${request.title}"吗？删除后不可恢复`,
        "删除需求",
        {
          confirmButtonText: "删除",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(() => {
          this.$axios({
            url: "demand/" + request.id,
            method: "DELETE",
          }).then(async (res) => {
            if (res.code == 200) {
              ElMessage({
                message: "删除成功",
                type: "success",
              });
              // 刷新需求列表
              await this.selectTab("requests");
            } else {
              ElMessage({
                message: res.msg || "删除失败",
                type: "error",
              });
            }
          });
        })
        .catch(() => {});
    },

    // 标记需求为解决
    resolveRequest(request) {
      ElMessageBox.confirm(
        `确定将需求"${request.title}"标记为已解决吗？`,
        "标记解决",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "success",
        }
      )
        .then(() => {
          this.$axios({
            url: `demand/update/status/${request.id}/1`,
            method: "GET",
          }).then(async (res) => {
            if (res.code == 200) {
              ElMessage({
                message: "标记成功",
                type: "success",
              });
              // 刷新需求列表
              await this.selectTab("requests");
            } else {
              ElMessage({
                message: res.msg || "操作失败",
                type: "error",
              });
            }
          });
        })
        .catch(() => {});
    },

    // 重新打开需求
    reopenRequest(request) {
      ElMessageBox.confirm(
        `确定重新打开需求"${request.title}"吗？`,
        "重新打开",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(() => {
          this.$axios({
            url: `demand/update/status/${request.id}/0`,
            method: "GET",
          }).then(async (res) => {
            if (res.code == 200) {
              ElMessage({
                message: "已重新打开",
                type: "success",
              });
              // 刷新需求列表
              await this.selectTab("requests");
            } else {
              ElMessage({
                message: res.msg || "操作失败",
                type: "error",
              });
            }
          });
        })
        .catch(() => {});
    },

    toggleFavorite(id) {
      this.$axios({
        url: "aitool/action/fav/" + id,
        method: "DELETE",
      }).then(async (res) => {
        if (res.code != 200) {
          ElMessage.error(res.msg);
        } else {
          await this.initData();
        }
      });
    },
    initUserInfo() {
      if (this.user) {
        this.userInfo.avatar = this.user.avatar;
        this.userInfo.username = this.user.name || this.user.username;
        this.userInfo.email = this.user.email || "techmaster@example.com"; // Mock email if missing
      }
    },
    async saveSettings() {
      // 修改密码：填了新密码时调用后端真实接口
      if (this.userInfo.newPassword) {
        if (!this.userInfo.currentPassword) {
          ElMessage.warning("请先填写当前密码");
          return;
        }
        const pwd = this.userInfo.newPassword;
        if (pwd.length < 6 || pwd.length > 15) {
          ElMessage.warning("新密码长度需为 6-15 个字符");
          return;
        }
        if (/\s/.test(pwd)) {
          ElMessage.warning("新密码不能包含空格");
          return;
        }
        const kinds = [/a-zA-Z/, /\d/, /[^a-zA-Z\d]/].filter((re) => re.test(pwd)).length;
        if (kinds < 2) {
          ElMessage.warning("新密码需包含数字、字母、特殊字符中的至少 2 种");
          return;
        }
        try {
          const data = await this.$aesEncrypt({
            currentPassword: this.userInfo.currentPassword,
            newPassword: this.userInfo.newPassword,
          });
          const res = await this.$axios({
            url: "user/update/password",
            method: "POST",
            data: { data },
          });
          if (res.code == 200) {
            ElMessage.success("密码修改成功");
            this.userInfo.currentPassword = "";
            this.userInfo.newPassword = "";
          } else {
            ElMessage.error(res.msg || "密码修改失败");
            return;
          }
        } catch (e) {
          ElMessage.error("密码修改失败，请稍后重试");
          return;
        }
      }

      // Simulate API call
      ElMessage.success("个人信息保存成功");

      // Update Vuex
      if (this.$store) {
        this.$store.commit("setUserInfo", {
          ...this.user,
          name: this.userInfo.username,
          avatar: this.userInfo.avatar,
        });
      }

      // Update Global State (for Navbar)
      if (globalState && globalState.user) {
        globalState.user.username = this.userInfo.username;
        globalState.user.avatar = this.userInfo.avatar;
      }
    },

    /**
     * 触发头像上传选择框
     */
    triggerAvatarUpload() {
      this.$refs.avatarInput.click();
    },

    /**
     * 处理头像上传
     * @param {Event} e - 文件选择事件
     */
    async handleAvatarUpload(e) {
      const file = e.target.files[0];
      if (!file) return;

      // 验证文件类型和大小
      const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
      if (!validTypes.includes(file.type)) {
        ElMessage.error("请上传 PNG、JPG、JPEG 或 WEBP 格式的图片");
        return;
      }

      // 限制文件大小（上限由后台「上传设置」统一配置）
      const maxSize = imageMaxBytes();
      if (file.size > maxSize) {
        ElMessage.error(
          "图片大小不能超过 " + getUploadConfig().imageMaxMB + "MB"
        );
        return;
      }

      // 显示加载提示
      ElMessage.info({
        message: "正在上传头像...",
        id: "avatar-upload",
      });

      try {
        // 创建FormData对象用于文件上传
        const formData = new FormData();
        formData.append("file", file);
        formData.append("UploadType", "Files");
        formData.append(
          "businessType",
          "avatar/" + new Date().format("yyyy/MM/dd"),
        );

        // 上传图片到服务器（请替换为你的实际上传接口）
        const res = await this.$axios({
          url: "upload", // 图片上传接口
          method: "POST",
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data", // 必须设置的请求头
          },
        });

        // 假设服务器返回 { code: 200, data: { url: "https://xxx.com/xxx.jpg" } }
        if (res.code === 200) {
          console.log(JSON.stringify(res));
          var avatar = res.data.uploads.files[0].o.fullPath; // 保存服务器返回的图片URL

          var dataStr = await this.$aesEncrypt({
            uid: this.user.uid,
            avatar: avatar,
          });
          this.$axios({
            url: "user/update/avatar",
            method: "POST",
            data: {
              data: dataStr,
            },
          }).then((res) => {
            if (res.code == 200) {
              this.$store.commit("setUserInfo", {
                ...this.user,
                avatar: avatar,
              });
              // 更新本地用户信息
              this.userInfo.avatar = avatar;
            } else {
              console.error("头像上传失败");
            }
          });
        } else {
          this.uploadError = res.message || "图片上传失败";
        }
      } catch (error) {
        console.error("图片上传失败:", error);
        // ElMessage.close("avatar-upload");
        console.error("头像上传失败:", error);
        ElMessage.error("头像上传失败，请稍后重试");
      } finally {
        e.target.value = "";
        // ElMessage.close("avatar-upload");
      }
    },
  },
};
Date.prototype.format = function (fmt) {
  var o = {
    "M+": this.getMonth() + 1, //月份
    "d+": this.getDate(), //日
    "h+": this.getHours(), //小时
    "m+": this.getMinutes(), //分
    "s+": this.getSeconds(), //秒
    "q+": Math.floor((this.getMonth() + 3) / 3), //季度
    S: this.getMilliseconds(), //毫秒
  };
  if (/(y+)/.test(fmt))
    fmt = fmt.replace(
      RegExp.$1,
      (this.getFullYear() + "").substr(4 - RegExp.$1.length),
    );
  for (var k in o)
    if (new RegExp("(" + k + ")").test(fmt))
      fmt = fmt.replace(
        RegExp.$1,
        RegExp.$1.length == 1 ? o[k] : ("00" + o[k]).substr(("" + o[k]).length),
      );
  return fmt;
};
</script>

<style scoped>
.hover-lift {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.hover-lift:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
</style>
